import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useQuery } from '@tanstack/react-query'

import { apiClient, AuthorRole, Message } from 'lib/apiClient'
import { QueryKey } from 'lib/queryClient'
import { convertDialogueSnapshotsToMessageList } from 'lib/utils/messages'

export interface AssistantChatMessagesHook {
  chatMessages: Message[]
  addChatMessageToThread: (message: Message) => void
  isLoading: boolean
  isError: boolean
  latestAssistantChatMessage: Message | null
  setLatestAssistantChatMessage: Dispatch<SetStateAction<Message | null>>
}

interface AssistantChatMessagesHookProps {
  correlationId: string | null
  initialAssistantResponseMessage: Message | null
  enabled?: boolean
}

export function useAssistantChatMessages({
  correlationId,
  initialAssistantResponseMessage,
  enabled = true,
}: AssistantChatMessagesHookProps): AssistantChatMessagesHook {
  const [chatMessages, setChatMessages] = useState<Message[] | null>(null)
  const [latestAssistantChatMessage, setLatestAssistantChatMessage] =
    useState<Message | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: [QueryKey.getDialogueSnapshot, correlationId],
    queryFn: async () =>
      apiClient.getSnapshot(correlationId ? [correlationId] : [], null, true),
    enabled: enabled && Boolean(correlationId),
  })

  useEffect(() => {
    if (data) {
      const conversation = convertDialogueSnapshotsToMessageList(data.dialogueSnapshots)
      if (initialAssistantResponseMessage) {
        conversation.push(initialAssistantResponseMessage)
        setLatestAssistantChatMessage(initialAssistantResponseMessage)
      }
      setChatMessages(conversation)
    }
  }, [data, initialAssistantResponseMessage])

  const addChatMessageToThread = useCallback((message: Message) => {
    if (message.authorRole === AuthorRole.Agent) {
      setLatestAssistantChatMessage(message)
    }
    setChatMessages(prev => (prev ? [...prev, message] : [message]))
  }, [])

  return {
    chatMessages: useMemo(() => chatMessages ?? [], [chatMessages]),
    addChatMessageToThread,
    isLoading: isLoading || (Boolean(correlationId) && !chatMessages),
    isError,
    latestAssistantChatMessage,
    setLatestAssistantChatMessage,
  }
}
